import type { ICampaignGroup, ICampaignGroupTemplate } from '~/modules/campaign-group/types';
import type { IInitializeCampaignGroup } from '~/modules/campaign-group/hooks';

import {
  useMutationCampaignGroupInfo,
  useMutationCampaignGroupState,
  useMutationCGSettings,
} from '~/modules/campaign-group/hooks';
import { useUpdateCampaignVariant } from '~/modules/campaign/hooks/useUpdateCampaignVariant';
import { CAMPAIGN_GROUP_MSG } from '~/modules/campaign-group/constants';
import { useCampaignGroupStore } from '~/modules/campaign-group/stores';
import { ICampaignType } from '~/modules/campaign/types';
import { CAMPAIGN_STATUS } from '~/constants/campaign';
import { detectCGInfoChanged } from '../helpers';
import { useCallback, useState } from 'react';
import { useToast } from '~/hooks/useToast';
import { deepCopy } from '~/utils';

interface IUseInitializeCGTemplateUpdaterProps {
  initializeCampaignGroup: IInitializeCampaignGroup;
  isShowDiscard: boolean;
}

interface IUseInitializeCGTemplateUpdaterReturn {
  isLoadingUpdateAll: boolean;
  isLoadingUpdateCGSettings: boolean;
  handleUpdateCGSettings: (options?: { campaignData?: ICampaignGroup }) => Promise<void>;
  handleUpdateCampaignGroup: () => Promise<void>;
}

export const useInitializeCGTemplateUpdater = ({
  initializeCampaignGroup,
}: IUseInitializeCGTemplateUpdaterProps): IUseInitializeCGTemplateUpdaterReturn => {
  const [isLoadingUpdateAll, setIsLoadingUpdateAll] = useState(false);
  const [isLoadingUpdateCGSettings, setIsLoadingUpdateCGSettings] = useState(false);

  const findCampaignGroupById = useCampaignGroupStore((state) => state.findCampaignGroupById);
  const upsertCampaignGroup = useCampaignGroupStore((state) => state.upsertCampaignGroup);

  const { shopifyToast } = useToast();
  const { onUpdate: onUpdateCGInfo } = useMutationCGSettings<ICampaignGroup>();
  const { onUpdate: onUpdateCampaignGroup } =
    useMutationCampaignGroupInfo<ICampaignGroupTemplate>();
  const { updateCampaign } = useUpdateCampaignVariant();
  const { handleUpdateCampaignGroupState } = useMutationCampaignGroupState();

  const handleUpdateCGSettings = useCallback(
    async (options?: { campaignData?: ICampaignGroup }) => {
      const { campaignGroup, handleSetCampaignGroup } = initializeCampaignGroup;
      if (!options?.campaignData) return;

      const campaignUpdate = options?.campaignData;
      const campaignUpdateData: Omit<ICampaignGroupTemplate, 'id'> = {
        type: ICampaignType.TEMPLATE_BUNDLE,
        targetDevices: campaignUpdate.targetDevices,
        targetVisitorTypes: campaignUpdate.targetVisitorTypes,
        targetChannels: campaignUpdate.targetChannels,
        trafficRate: campaignUpdate.trafficRate,
        priority: campaignUpdate.priority,
      };

      try {
        setIsLoadingUpdateCGSettings(true);

        const dataUpdate: ICampaignGroupTemplate = {
          ...campaignGroup,
          ...campaignUpdateData,
          id: campaignGroup?.id || '',
          type: ICampaignType.TEMPLATE_BUNDLE,
        };
        handleSetCampaignGroup(dataUpdate);
        await onUpdateCampaignGroup(campaignUpdate.id, campaignUpdateData);
      } catch (error) {
        throw new Error((error as Error).message);
      } finally {
        setIsLoadingUpdateCGSettings(false);
      }
    },
    [initializeCampaignGroup, onUpdateCampaignGroup],
  );

  const handleUpdateCampaignGroup = async () => {
    const { campaignGroup, handleSetCampaignGroup } = initializeCampaignGroup;
    if (!campaignGroup) return;

    const storedCampaignGroup = findCampaignGroupById(campaignGroup.id);
    if (!storedCampaignGroup) return;

    const campaignGroupTemplate = campaignGroup as ICampaignGroupTemplate;
    const storedCampaignGroupTemplate = storedCampaignGroup as ICampaignGroupTemplate;
    const campaigns = campaignGroupTemplate.campaigns || [];

    const hasCGInfoChanged = detectCGInfoChanged(campaignGroupTemplate, storedCampaignGroup);

    setIsLoadingUpdateAll(true);

    try {
      const isRunning = campaignGroupTemplate.status === CAMPAIGN_STATUS.RUNNING;
      if (isRunning) {
        await handleUpdateCampaignGroupState(
          { ...campaignGroup, status: CAMPAIGN_STATUS.PAUSED },
          { hideSuccessMessage: true },
        );
      }

      if (hasCGInfoChanged) {
        await onUpdateCGInfo(campaignGroup);
      }

      const updatedCampaigns = await Promise.all(
        campaigns.map((campaign) => {
          const storedCampaign = storedCampaignGroupTemplate.campaigns?.find(
            (c) => c.id === campaign.id,
          );
          return updateCampaign(campaign, { storedCampaign, isCGTemplate: true });
        }),
      );

      const updatedCampaignGroup: ICampaignGroupTemplate = {
        ...campaignGroupTemplate,
        campaigns: updatedCampaigns,
      };

      shopifyToast(CAMPAIGN_GROUP_MSG.UPDATE.SUCCESS);

      if (isRunning) {
        await handleUpdateCampaignGroupState(
          { ...campaignGroup, status: CAMPAIGN_STATUS.RUNNING },
          { hideSuccessMessage: true },
        );
        return;
      }

      upsertCampaignGroup(deepCopy(updatedCampaignGroup));
      handleSetCampaignGroup(deepCopy(updatedCampaignGroup));
    } finally {
      setIsLoadingUpdateAll(false);
    }
  };

  return {
    isLoadingUpdateAll,
    isLoadingUpdateCGSettings,
    handleUpdateCGSettings,
    handleUpdateCampaignGroup,
  };
};

export type IInitializeCGUpdater = ReturnType<typeof useInitializeCGTemplateUpdater>;
